import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Button,
  useTheme,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  Skeleton,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useDispatch, useSelector } from "react-redux";
import VantageListHead from "../../../component/ListHead/SignalList";
import { TABLE_HEAD_SIGNAL_HISTORICAL, applySortFilter, getComparator } from "../../../component/Methods";
import { toast } from "react-toastify";
import { selectHistoricalSignalSet ,selectActiveRequests} from "../../../Redux/Reducers/selectors";
import HistoricalSignalsSummary from "./HistoricalSignalsSummary";
import {
  historicalSignalSetsForStrategy,
  toLegacyWeights,
  requestHistoricalSignalSet,
} from "../../../utils/historicalSignalSets";

const PAGE_SIZE = 18;

function signalSetKey(signalSet, index) {
  return `${signalSet?.date ?? "na"}-${signalSet?.user ?? ""}-${index}`;
}

function LoadMoreSkeletonCard({ theme }) {
  return (
    <Box
      sx={{
        borderRadius: "4px",
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        overflow: "hidden",
        minHeight: 280,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Skeleton variant="text" width={160} height={28} />
        <Skeleton variant="rectangular" width={72} height={32} sx={{ borderRadius: 1 }} />
      </Box>
      <Box sx={{ p: 2 }}>
        <Skeleton variant="rectangular" width="100%" height={200} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
  );
}

const HistoricalSignalsList = ({
  strategies,
  strategyValObj,
  strategyVal,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [historicalData, setHistoricalData] = useState([]);
  const [rawHistoricalData, setRawHistoricalData] = useState([]);
  const [sortStates, setSortStates] = useState({});
  const [copyMenuAnchor, setCopyMenuAnchor] = useState(null);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [currentSignalSetIndex, setCurrentSignalSetIndex] = useState(null);
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const wasFetchingApi = useRef(false);
  const processedCacheRef = useRef(new Map());
  const prevTotalRef = useRef(0);
  const loadMoreTimeoutRef = useRef(null);

  const historicalSignalSet = useSelector(selectHistoricalSignalSet);
  const activeRequests = useSelector(selectActiveRequests);
  const isFetchingHistoricalSignals = activeRequests?.includes("get_historical_signal_set") || false;

  const filteredSets = historicalSignalSetsForStrategy(
    historicalSignalSet,
    strategyValObj
  );
  const totalSets = filteredSets.length;
  const hasMoreApi =
    !!historicalSignalSet?.hasMore && !!historicalSignalSet?.nextCursor;

  useEffect(() => {
    return () => {
      if (loadMoreTimeoutRef.current) clearTimeout(loadMoreTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setDisplayCount(PAGE_SIZE);
    setSortStates({});
    setIsLoadingMore(false);
    wasFetchingApi.current = false;
    processedCacheRef.current = new Map();
    prevTotalRef.current = 0;
    if (loadMoreTimeoutRef.current) {
      clearTimeout(loadMoreTimeoutRef.current);
      loadMoreTimeoutRef.current = null;
    }
  }, [strategyValObj]);

  useEffect(() => {
    if (!totalSets) setDisplayCount(PAGE_SIZE);
  }, [totalSets]);

  useEffect(() => {
    if (isFetchingHistoricalSignals) {
      wasFetchingApi.current = true;
      return;
    }
    if (wasFetchingApi.current && totalSets) {
      setDisplayCount(totalSets);
      wasFetchingApi.current = false;
    }
  }, [isFetchingHistoricalSignals, totalSets]);

  useEffect(() => {
    if (!isLoadingMore) {
      prevTotalRef.current = totalSets;
      return;
    }
    if (totalSets !== prevTotalRef.current) {
      setDisplayCount(totalSets);
      setIsLoadingMore(false);
      prevTotalRef.current = totalSets;
      if (loadMoreTimeoutRef.current) {
        clearTimeout(loadMoreTimeoutRef.current);
        loadMoreTimeoutRef.current = null;
      }
    }
  }, [isLoadingMore, totalSets]);

  useEffect(() => {
    if (filteredSets.length > 0 && strategyValObj) {
      const sortedData = [...filteredSets].sort((a, b) => b.date - a.date);
      const visible = sortedData.slice(0, displayCount);
      const cache = processedCacheRef.current;
      setRawHistoricalData(
        visible.map((signalSet) => {
          const cacheKey = signalSetKey(signalSet, signalSet.date);
          let rawSignals = cache.get(cacheKey);
          if (!rawSignals) {
            rawSignals = processHistoricalSignals(signalSet);
            cache.set(cacheKey, rawSignals);
          }
          return {
            ...signalSet,
            rawSignals,
          };
        })
      );
    } else {
      setRawHistoricalData([]);
    }
  }, [historicalSignalSet, strategyValObj, displayCount]);

  // Apply sorting to signals within each signal set based on individual sort states (don't reset pagination)
  useEffect(() => {
    if (rawHistoricalData.length > 0) {
      const sortedData = rawHistoricalData.map((signalSet, index) => {
        // Get sort state for this signal set, default to desc/value if not set
        const sortState = sortStates[index] || { order: "desc", orderBy: "value" };
        return {
          ...signalSet,
          processedSignals: applySortFilter(
            signalSet.rawSignals,
            getComparator(sortState.order, sortState.orderBy)
          )
        };
      });
      setHistoricalData(sortedData);
    } else {
      setHistoricalData([]);
    }
  }, [rawHistoricalData, sortStates]);



  const processHistoricalSignals = (signalData) => {
    const { weights_keys, weights_values } = toLegacyWeights(signalData);
    if (!weights_keys || !weights_values || !Object.keys(weights_values).length) {
      return [];
    }

    const signals = [];
    const { performance_keys, performance_values } = signalData;

    Object.keys(weights_values).forEach(key => {
      const weight = weights_values[key];
      const tickerInfo = weights_keys[key];
      
      if (tickerInfo && tickerInfo.symbol) {
        // Get performance data for this ticker
        let performance = null;
        if (performance_keys && performance_values) {
          // Find matching performance key for this ticker
          const perfKey = Object.keys(performance_keys).find(perfKey => {
            const perfTickerInfo = performance_keys[perfKey];
            return perfTickerInfo && perfTickerInfo.symbol && 
                   perfTickerInfo.symbol.toUpperCase() === tickerInfo.symbol.toUpperCase();
          });
          
          if (perfKey && performance_values[perfKey] !== undefined) {
            performance = performance_values[perfKey];
          }
        }

        signals.push({
          key,
          name: tickerInfo.symbol.toUpperCase(),
          value: (weight * 100).toFixed(4),
          long_short: weight >= 0 ? "L" : "S",
          performance: performance !== null ? (performance * 100).toFixed(2) : null,
        });
      }
    });

    // Return unsorted signals - sorting will be handled by applySortFilter
    return signals;
  };

  const handleRequestSort = (event, property, signalSetIndex) => {
    setSortStates(prev => {
      const currentSort = prev[signalSetIndex] || { order: "desc", orderBy: "value" };
      const isAsc = currentSort.orderBy === property && currentSort.order === "asc";
      return {
        ...prev,
        [signalSetIndex]: {
          order: isAsc ? "desc" : "asc",
          orderBy: property
        }
      };
    });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    return date.toLocaleString('en-US', options);
  };

  const handleCopyMenuOpen = (event, signalSetIndex) => {
    setCopyMenuAnchor(event.currentTarget);
    setCopyMenuOpen(true);
    setCurrentSignalSetIndex(signalSetIndex);
  };

  const handleCopyMenuClose = () => {
    setCopyMenuOpen(false);
    setCopyMenuAnchor(null);
    setCurrentSignalSetIndex(null);
  };

  const handleCopyForWebsite = () => {
    if (currentSignalSetIndex !== null && historicalData[currentSignalSetIndex]) {
      const signals = historicalData[currentSignalSetIndex].processedSignals;
      if (!signals || signals.length === 0) {
        toast.error("No signals to copy");
        return;
      }

      try {
        const jsonData = JSON.stringify(signals);
        navigator.clipboard.writeText(jsonData);
      } catch (error) {
        console.error("Error copying signals:", error);
        toast.error("Failed to copy signals");
      }
    }
    handleCopyMenuClose();
  };

  const handleCopyForExcel = () => {
    if (currentSignalSetIndex !== null && historicalData[currentSignalSetIndex]) {
      const signals = historicalData[currentSignalSetIndex].processedSignals;
      if (!signals || signals.length === 0) {
        toast.error("No signals to copy");
        return;
      }

      try {
        let formattedData = signals
          .map((item) => `${item.name}\t${item.value}%`)
          .join("\n");
        navigator.clipboard.writeText(formattedData);
      } catch (error) {
        console.error("Error copying signals:", error);
        toast.error("Failed to copy signals");
      }
    }
    handleCopyMenuClose();
  };

  const handleLoadMore = () => {
    if (isFetchingHistoricalSignals || isLoadingMore) return;
    if (displayCount < totalSets) {
      setDisplayCount((count) => Math.min(count + PAGE_SIZE, totalSets));
      return;
    }
    const cursor = historicalSignalSet?.nextCursor;
    if (!hasMoreApi || !cursor || !strategyValObj) return;

    prevTotalRef.current = totalSets;
    setIsLoadingMore(true);
    if (loadMoreTimeoutRef.current) clearTimeout(loadMoreTimeoutRef.current);
    loadMoreTimeoutRef.current = setTimeout(() => {
      setIsLoadingMore(false);
      loadMoreTimeoutRef.current = null;
    }, 30000);

    dispatch(
      requestHistoricalSignalSet({
        strategy: strategyValObj,
        fetchAllSignalsets: historicalSignalSet.fetchAllSignalsets,
        fetchPerformance: false,
        fetchBacktestSummary: false,
        cursor,
        replace: false,
        silent: true,
      })
    );
  };

  const displayedData = historicalData;
  const showLoadMore = displayCount < totalSets || hasMoreApi;

  if (!strategyVal) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          color: theme.palette.text.secondary,
        }}
      >
        <Typography variant="h5">
          Please select a strategy to view historical signals
        </Typography>
      </Box>
    );
  }

  if (isFetchingHistoricalSignals && historicalData.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          color: theme.palette.text.secondary,
        }}
      >
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h5">
          Fetching historical signals...
        </Typography>
      </Box>
    );
  }

  if (!historicalData || historicalData.length === 0) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 8,
          color: theme.palette.text.secondary,
        }}
      >
        <Typography variant="h5">
          No historical signals found for the selected strategy
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 3,
          width: "100%",
        }}
      >
        {displayedData.map((signalSet, signalSetIndex) => (
          <Box
            key={signalSetKey(signalSet, signalSetIndex)}
            sx={{
              borderRadius: "4px",
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              overflow: "hidden",
            }}
          >
            {/* Header with Date and Copy Button */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                backgroundColor: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {formatDate(signalSet.date)}{' '}
                <Typography
                  component="span"
                  variant="subtitle1"
                  sx={{
                    fontWeight: 400,
                    color: theme.palette.text.secondary,
                    opacity: 0.7,
                    fontSize: '0.875rem',
                  }}
                >
                  Local
                </Typography>
              </Typography>
              <Tooltip title="Copy Signals" arrow placement="top">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={(event) => handleCopyMenuOpen(event, signalSetIndex)}
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: theme.palette.primary.dark,
                      backgroundColor: theme.palette.primary.light + "20",
                      transform: "scale(1.02)",
                    },
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  Copy
                </Button>
              </Tooltip>
            </Box>

            {/* Content Area with Summary and Table */}
            <Box sx={{ p: 2 }}>
              <HistoricalSignalsSummary 
                signalData={signalSet} 
                isLatestSignal={signalSetIndex === 0}
                nextSignalDate={signalSetIndex > 0 ? displayedData[signalSetIndex - 1].date : null}
              />
              
              <TableContainer style={{ height: "400px", marginTop: "8px" }}>
                <Table
                  size="small"
                  stickyHeader
                  aria-label="a dense table"
                  sx={{
                    height: "max-content",
                  }}
                >
                  <VantageListHead
                    order={sortStates[signalSetIndex]?.order || "desc"}
                    orderBy={sortStates[signalSetIndex]?.orderBy || "value"}
                    headLabel={TABLE_HEAD_SIGNAL_HISTORICAL}
                    onRequestSort={(event, property) => handleRequestSort(event, property, signalSetIndex)}
                    noStatus={true}
                    customizeFont={12}
                  />
                  <TableBody>
                    {signalSet.processedSignals?.map((row, index) => (
                      <TableRow
                        key={row?.key}
                        sx={{
                          overflow: "auto",
                          "&:hover": {
                            background: theme.palette.grey[200],
                            cursor: "pointer",
                          },
                          "& > .MuiTableCell-root": {
                            paddingTop: "1px",
                            paddingBottom: "0px !important",
                            lineHeight: "1.2",
                          },
                        }}
                      >
                        <TableCell
                          align="left"
                          sx={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            color: theme.palette.text.primary,
                            borderRight: "none",
                            cursor: "pointer",
                          }}
                        >
                          {row?.name}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontSize: "12px",
                            paddingLeft: "5px",
                            color: theme.palette.text.primary,
                            borderLeft: "none",
                            borderRight: "none",
                          }}
                        >
                          {row?.value}%
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            fontSize: "12px",
                            paddingLeft: "10px",
                            color: theme.palette.text.primary,
                            borderLeft: "none",
                            height: "21.5px",
                          }}
                        >
                          {row?.long_short}
                        </TableCell>
                       
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        ))}
        {isLoadingMore &&
          [0, 1, 2].map((slot) => (
            <LoadMoreSkeletonCard key={`paging-skeleton-${slot}`} theme={theme} />
          ))}
      </Box>

      {showLoadMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 4,
            mb: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={isFetchingHistoricalSignals || isLoadingMore}
            startIcon={
              isLoadingMore ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 500,
              minWidth: 140,
            }}
          >
            {isLoadingMore ? "Loading..." : "Load More"}
          </Button>
        </Box>
      )}

      {/* Copy Menu */}
      <Menu
        anchorEl={copyMenuAnchor}
        open={copyMenuOpen}
        onClose={handleCopyMenuClose}
        MenuListProps={{
          "aria-labelledby": "copy-button",
        }}
      >
        <MenuItem onClick={handleCopyForWebsite}>
          Copy for Website
        </MenuItem>
        <MenuItem onClick={handleCopyForExcel}>
          Copy for Excel
        </MenuItem>
      </Menu>
    </>
  );
};

export default HistoricalSignalsList;
